<template>
  <div>
    <v-row v-if="registrationEnabled">
      <v-col>
        <v-btn
          @click="updateQuery({ mode: 'signup' })"
          :ripple="false"
          class="fs-16 fw-500 pointer c-primary bc-link-hover px-0 underlined"
          data-cy="signup-mode-button"
          data-testid="signup-mode-button"
          variant="text"
        >
          {{ $bct('Or, create an account.') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="ssoEnabled">
      <v-col>
        <LoginWithSSO data-testid="login-with-sso" />
      </v-col>
    </v-row>

    <template v-if="magicLinkEnabled || passwordEnabled">
      <v-divider
        v-if="ssoEnabled"
        class="my-8"
      />

      <form @submit.prevent="login">
        <v-row>
          <template v-if="phoneAuthenticationEnabled">
            <LabeledSimpleSelect
              v-model="loginMode"
              @change="resetLoginFactors"
              :items="[
                { text: $bct('Email'), value: 'Email' },
                { text: $bct('Cell phone'), value: 'Cell phone' },
              ]"
              :message="'Email or phone'"
              :tile="false"
              :variant="'outlined'"
              cols="12"
              data-cy="login-mode-selector"
              data-testid="login-mode-selector"
              md="3"
              mandatory
            />
          </template>

          <LabeledTextfield
            v-show="loginMode === 'Email'"
            v-model="email"
            :md="phoneAuthenticationEnabled ? '9' : '12'"
            :message="'Email'"
            :tile="false"
            :variant="'outlined'"
            aria-label="Enter your email"
            autocomplete="off"
            cols="12"
            data-cy="login_email"
            data-testid="login_email"
            mandatory
          />

          <LabeledTextfield
            v-show="loginMode === 'Cell phone'"
            v-model="phone"
            :message="'Cell phone number'"
            :tile="false"
            :variant="'outlined'"
            aria-label="Enter phone number"
            cols="12"
            data-cy="login_phone"
            data-testid="login_phone"
            mask="(###) ###-####"
            md="9"
            placeholder="(555) 555-5555"
            mandatory
          />

          <template v-if="passwordEnabled">
            <LabeledTextfield
              v-show="!requestingLoginCode"
              v-model="password"
              :message="'Password'"
              :tile="false"
              :variant="'outlined'"
              aria-label="Enter password"
              data-cy="login_password"
              data-testid="login_password"
              type="password"
              mandatory
            />

            <v-col>
              <PasswordReset
                data-testid="password-reset"
                scope="parent"
              />
            </v-col>
          </template>
        </v-row>

        <v-row class="mt-3">
          <v-col>
            <v-btn
              :disabled="submitDisabled"
              :loading="processing"
              :ripple="false"
              class="c-white focus-very-visible"
              color="primary"
              data-cy="login-button"
              data-testid="login-button"
              size="x-large"
              type="submit"
              block
            >
              {{ $bct('Login') }}
            </v-btn>
          </v-col>
        </v-row>
      </form>

      <v-row
        v-if="passwordEnabled"
        class="d-flex align-center"
      >
        <v-col>
          <template v-if="requestingLoginCode">
            <v-btn
              @click="requestingLoginCode = false"
              :loading="processing"
              :ripple="false"
              color="primary"
              data-cy="toggle-password-button"
              data-testid="toggle-password-button"
              size="small"
              variant="outlined"
            >
              {{ $bct('Login with email and password.') }}
            </v-btn>
          </template>

          <template v-else-if="magicLinkEnabled">
            <v-btn
              @click="requestingLoginCode = true"
              :loading="processing"
              :ripple="false"
              color="primary"
              data-cy="toggle-code-button"
              data-testid="toggle-code-button"
              size="large"
              variant="outlined"
            >
              {{ $bct('Request one-time login code link.') }}
            </v-btn>
          </template>
        </v-col>
      </v-row>
    </template>

    <v-dialog
      v-model="challengeDialogIsVisible"
      :max-width="600"
    >
      <v-card
        border
        tile
      >
        <v-card-text class="pa-6">
          <v-row dense>
            <v-col class="d-flex justify-center">
              <p class="fs-16 fw-600">
                {{ $bct('Enter the one-time passcode sent to your device:') }}
              </p>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col class="d-flex justify-center">
              <v-otp-input
                v-model="code"
                @finish="createSession"
                :disabled="processing"
                class="mxw-400"
                data-cy="otp-input"
                data-testid="otp-input"
                length="6"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import LabeledSimpleSelect from '@/shared/components/form/LabeledSimpleSelect.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import LoginWithSSO from '@/shared/components/LoginWithSSO.vue';
import PasswordReset from '@/shared/components/PasswordReset.vue';
import { trimOtp } from '@/shared/utils/otp.js';
import useApi from '@/shared/composables/useApi';
import useEmailValidation from '@/shared/composables/useEmailValidation';
import useEventBus from '@/shared/composables/useEventBus';
import useRouterHelper from '@/shared/composables/useRouterHelper';

const { api } = useApi();
const { validEmail } = useEmailValidation();
const eventBus = useEventBus();
const route = useRoute();
const router = useRouter();
const { updateQuery } = useRouterHelper();
const store = useStore();
const $bct = inject('translator');

const challenge = ref(null);
const challengeDialogIsVisible = ref(false);
const code = ref(null);
const email = ref(null);
const loginMode = ref('Email');
const mfaAttempts = ref(0);
const password = ref(null);
const phone = ref(null);
const processing = ref(null);
const requestingLoginCode = ref(false);

const magicLinkEnabled = computed(() => {
  return store.state.config.enable_magic_link_authentication;
});

const passwordEnabled = computed(() => {
  return store.state.config.enable_password_authentication;
});

const phoneAuthenticationEnabled = computed(() => {
  return store.state.config.enable_phone_authentication;
});

const registrationEnabled = computed(() => {
  return !store.state.config.disable_self_registration;
});

const ssoEnabled = computed(() => {
  return store.state.site.sso_connections?.length > 0;
});

const submitDisabled = computed(() => {
  if (loginMode.value === 'Email' && !email.value) return true;

  if (loginMode.value === 'Cell phone' && !phone.value) return true;

  return false;
});

watch(code, (newValue) => trimOtp(code, newValue));

function login() {
  if (loginMode.value === 'Email' && !validEmail(email.value)) {
    eventBus.chime('Please enter a valid email, including the domain (e.g., user@example.com)');
    return;
  }

  if (passwordEnabled.value && !requestingLoginCode.value) {
    createSession();
  } else {
    createLoginCode();
  }
}

function createLoginCode() {
  processing.value = true;
  api.member.login_code.create(
    {
      is_parent: true,
      email: email.value,
      organization_id: window.organization_id,
      phone: phone.value,
      redirect_url: route.query.redirect_url,
      site_id: window.site_id,
    },
    async (response) => {
      processing.value = false;
      await router.push({
        name: 'CodeSent',
        query: {
          ...route.query,
          mode: 'login',
          phone: phone.value,
          token: response.data.code_id,
          email_sent_to: email.value,
        },
      });
    },
    (error) => {
      processing.value = false;
      eventBus.chime(error.response.data.errors[0]);
    },
  );
}

function createSession() {
  processing.value = true;
  api.member.session.create(
    {
      challenge_id: challenge.value,
      code: code.value,
      email: email.value,
      organization_id: window.organization_id,
      password: password.value,
      scope: 'parent',
    },
    (response) => {
      const { status } = response.data;
      processing.value = false;

      if (status === 'challenge') {
        challenge.value = response.data.challenge_id;
        challengeDialogIsVisible.value = true;
        code.value = null;
        mfaAttempts.value += 1;

        if (mfaAttempts.value > 1) {
          eventBus.chime(
            'Your previous code has expired. A new code has been sent to your device.',
          );
        }
      } else if (status === 'mfa_failed') {
        code.value = null;
        eventBus.chime('You entered an incorrect code. Please re-enter and try again.');
      } else if (status === 'authenticated') {
        store.dispatch('identify', {
          success: async () => {
            if (route.query.redirect_url) {
              await router.push(route.query.redirect_url);
            } else if (route.query.action) {
              window.localStorage.removeItem('redirect');

              if (route.query.action === 'apply') {
                await router.push({
                  name: 'ChildSubsidyNew',
                  params: {
                    subsidyProgramId: route.query.subsidyProgramId,
                  },
                });
              } else if (route.query.action === 'providerReferral') {
                await router.push({
                  name: 'ChildSubsidyNew',
                  params: {
                    subsidyProgramId: route.query.subsidyProgramId,
                  },
                  query: {
                    programId: route.query.programId,
                  },
                });
              } else if (route.query.action === 'familyApply') {
                await router.push({
                  name: 'FamilySubsidyNew',
                  params: { familySubsidyProgramId: route.query.familySubsidyProgramId },
                });
              } else if (
                route.query.action === 'form' &&
                route.query.schemaId &&
                route.query.formId
              ) {
                await router.push({
                  name: 'FormContinue',
                  params: {
                    schemaId: route.query.schemaId,
                    formId: route.query.formId,
                  },
                });
              } else if (route.query.action === 'continue' && route.query.subsidyId) {
                await router.push({
                  name: 'SubsidyContinue',
                  params: {
                    subsidyId: route.query.subsidyId,
                  },
                });
              } else {
                await router.push({ name: 'Dashboard' });
              }
            } else {
              await router.push({ name: 'Dashboard' });
            }
          },
          failure: () => {
            eventBus.chime('An unknown error has occurred. Please contact support for assistance.');
          },
        });
      } else {
        eventBus.longChime('An unknown error has occurred. Please contact support for assistance.');
      }
    },
    () => {
      processing.value = false;
      eventBus.chime('The password/email you entered is incorrect. Please try again.');
    },
  );
}

function resetLoginFactors() {
  email.value = null;
  phone.value = null;
}
</script>
